import { ref, onUnmounted } from 'vue'
import { useAuthStore } from '../stores/auth'
import logger from '../utils/logger'
import { isTeacherSession } from '../utils/isTeacherSession'

export interface TokenExpirationOptions {
  title?: string
  message?: string
  continueText?: string
  logoutText?: string
  onContinue?: () => void | Promise<void>
  onLogout?: () => void
  /** Seconds until auto-logout; should match remaining session time from the server. */
  countdownSeconds?: number
  /** @deprecated Use countdownSeconds */
  autoLogoutSeconds?: number
}

// Shared singleton state - ensures all components/services use the same reactive state
const sharedState = {
  showWarning: ref(false),
  warningOptions: ref<TokenExpirationOptions>({}),
  countdown: ref<number | null>(null)
}

let countdownInterval: NodeJS.Timeout | null = null

export function useTokenExpiration() {
  const isAuthenticated = (): boolean => {
    // Check if user is authenticated before showing modal
    try {
      const authStore = useAuthStore()
      return authStore.isAuthenticated || localStorage.getItem('isAuthenticated') === 'true'
    } catch {
      // Fallback to localStorage check if store is not available
      return localStorage.getItem('isAuthenticated') === 'true'
    }
  }

  const showTokenExpirationWarning = (options: TokenExpirationOptions = {}) => {
    // CRITICAL: Only show modal if user is authenticated
    if (!isAuthenticated()) {
      logger.debug('User is not authenticated - not showing token expiration warning')
      return
    }
    if (isTeacherSession()) {
      logger.debug('Teacher session - skipping token expiration warning modal')
      return
    }
    // Show once per warning cycle — do not reset countdown on repeated checks
    if (sharedState.showWarning.value) {
      logger.debug('Session expiration modal already visible - skipping duplicate show')
      return
    }

    const countdownSeconds =
      options.countdownSeconds ?? options.autoLogoutSeconds ?? 0

    sharedState.warningOptions.value = {
      title: 'Session Expiring Soon',
      message: 'Your session will expire soon. Would you like to continue your session?',
      continueText: 'Yes, Continue',
      logoutText: 'No, Logout',
      ...options,
      countdownSeconds,
    }
    sharedState.showWarning.value = true

    if (countdownSeconds > 0) {
      sharedState.countdown.value = countdownSeconds

      countdownInterval = setInterval(() => {
        if (sharedState.countdown.value === null || sharedState.countdown.value <= 0) {
          return
        }
        sharedState.countdown.value--
        if (sharedState.countdown.value === 0) {
          if (countdownInterval) {
            clearInterval(countdownInterval)
            countdownInterval = null
          }
          try {
            if (sharedState.warningOptions.value.onLogout) {
              logger.warn('Auto-logout triggered - session countdown reached zero')
              sharedState.warningOptions.value.onLogout()
            }
          } finally {
            hideWarning()
          }
        }
      }, 1000)
    } else {
      sharedState.countdown.value = null
    }

    logger.debug('Token expiration warning modal shown', sharedState.warningOptions.value)
  }

  const hideWarning = () => {
    // Clear countdown when hiding
    if (countdownInterval) {
      clearInterval(countdownInterval)
      countdownInterval = null
    }
    sharedState.countdown.value = null
    sharedState.showWarning.value = false
  }

  const handleContinue = async () => {
    try {
      if (sharedState.warningOptions.value.onContinue) {
        await sharedState.warningOptions.value.onContinue()
      }
    } finally {
      hideWarning()
    }
  }

  const handleLogout = () => {
    try {
      if (sharedState.warningOptions.value.onLogout) {
        sharedState.warningOptions.value.onLogout()
      }
    } finally {
      hideWarning()
    }
  }

  return {
    showWarning: sharedState.showWarning,
    warningOptions: sharedState.warningOptions,
    countdown: sharedState.countdown,
    showTokenExpirationWarning,
    hideWarning,
    handleContinue,
    handleLogout
  }
}
